import { NewTag } from "@/components/Tag";
import { canonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { HeroSection } from "@/containers/HeroSection";
import { Levels } from "@/design-system/Evolution/Levels";
import { storeSignupContext } from "@/events/signupTracking";
import { EvoDetailPanel } from "@/features/Evolutions/EvoDetailPanel";
import { EvoFacts } from "@/features/Evolutions/EvoFacts";
import { EvoRecommended } from "@/features/Evolutions/EvoRecommended";
import { useUpgradeFormatter } from "@/features/Evolutions/useUpgradeFormatter";
import { useGoBack } from "@/hooks/useGoBack";
import { evoPathwayIcon } from "@/icons";
import {
  evolutionCategoriesByIdSelector,
  getEvolutionByIdSelector,
} from "@/redux/evolutions/evolutionsSlice";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import {
  defaultPathwayPath,
  isPathwayEvolution,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { capitalizeWordsAndRemoveDashes } from "@repo/shared/utils";
import { DnaIcon } from "lucide-react";
import { MouseEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { EvoDataRow } from "../../EvoDataRow";
import { ChangeStatesPopup } from "../../Popups/ChangeStatesPopup";

export const DetailedEvoPage = () => {
  const { t } = useTranslation("evolutions");
  const { evoId } = useParams();
  const { user } = useSelector(getUserSelector);
  const evo = useSelector(getEvolutionByIdSelector(Number(evoId)));
  const categoriesById = useSelector(evolutionCategoriesByIdSelector);
  // Declared as a flat sibling of "overview", so there is no parent route to
  // resolve, and the Builder links here with target="_blank".
  const goBack = useGoBack({ fallback: "/evolutions/overview" });
  const [showChangeStateModal, setShowChangeStateModal] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const category = evo ? categoriesById[evo.categoryId] : undefined;
  const { name } = useParams();
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  // Explorative only: which path per level, reset with the evolution. Never
  // persisted, so reloading is back to path 1 everywhere.
  const [pathwayPath, setPathwayPath] = useState<number[]>([]);
  useEffect(() => {
    setPathwayPath(evo ? defaultPathwayPath(evo) : []);
  }, [evo]);

  const formatUpgrade = useUpgradeFormatter();

  const handleCompleteChangeState = () => {
    setShowChangeStateModal(false);
  };

  const handleChangeStateClick = (e: MouseEvent) => {
    // Stop the opening click from reaching the document listener of the
    // popup's useOutsideClick, which would close it the same tick.
    e.stopPropagation();
    if (user?.uuid) {
      setShowChangeStateModal(true);
    } else {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "change-evo-state",
        }),
      );
      // Override the generic hook-derived signup context with the tracking-plan
      // value so user_signed_up is attributed to this gate.
      storeSignupContext("change_evo_state", {
        section: "Evolutions",
        subsection: "Evo View",
      });
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <HeroSection
        helmetTitle={
          `EA ${GAME_NAME} Evolution: ` +
          (evo?.name || capitalizeWordsAndRemoveDashes(name) || "")
        }
        helmetDescription={evo?.description}
        helmetCanonicalLink={canonicalUrl()}
        back={goBack}
        shouldBeInTheDocumentFlow
      />
      {evo && (
        // De-boxed: the page itself is the surface — sections separated by
        // whitespace only, no hairlines, no card wrapper.
        <div className="mx-auto flex w-full max-w-content flex-col gap-8 px-4 pt-5 pb-12 mobile:gap-10 mobile:px-8 mobile:pt-6 touch-device:pt-8">
          <div className="flex flex-col gap-4">
            <div className="flex min-w-0 items-center gap-2">
              <div className="size-6 shrink-0 text-evolution-new">
                {isPathwayEvolution(evo) ? (
                  evoPathwayIcon
                ) : (
                  <DnaIcon size={24} />
                )}
              </div>
              <h2 className="min-w-0 truncate text-xl font-semibold text-foreground">
                {evo.name}
              </h2>
              {evo.newEvolution && (
                <div className="shrink-0">
                  <NewTag />
                </div>
              )}
            </div>
            {/* A hairline that fades out at both ends, so it reads as a pause
                rather than a rule across the page. */}
            <div className="h-px bg-linear-to-r from-transparent via-edge-strongest to-transparent" />
            <EvoFacts
              evolution={evo}
              category={category}
              // Changing the state by hand is retired for now; kept to bring
              // back if users ask for it.
              // onChangeState={handleChangeStateClick}
            />
          </div>

          <EvoRecommended evolution={evo} subsection="Evo View" />

          <EvoDetailPanel
            evolution={evo}
            category={category}
            pathwayPath={pathwayPath}
            collapsible={isMobile}
          />
          <Levels
            evolution={evo}
            pathwayPath={pathwayPath}
            onChange={setPathwayPath}
            layout={isMobile ? "scroll" : "wrap"}
            formatUpgrade={formatUpgrade}
          />
        </div>
      )}
      {showChangeStateModal && (
        <ChangeStatesPopup
          onComplete={handleCompleteChangeState}
          selectedEvolutions={[evo]}
          onClose={() => setShowChangeStateModal(false)}
        />
      )}
    </main>
  );
};
